import { buildFileUploadData, type FileUploadData } from './file-upload';

export type DemoFileEntry = {
  id: string;
  label: string;
  /** Fetched and uploaded together as a single selection. */
  urls: string[];
};

export const DEMO_FILES: DemoFileEntry[] = [
  {
    id: 'p28',
    label: 'P2.8 – sample_p28.psdat',
    urls: ['/data/sample_p28.psdat'],
  },
  {
    id: 'p3',
    label: 'P3 – sample_p3.pdat',
    urls: ['/data/sample_p3.pdat'],
  },
  {
    id: 'p32',
    label: 'P3.2 – sample_p32.pvdat',
    urls: ['/data/sample_p32.pvdat'],
  },
  {
    id: 'p5',
    label: 'P5 – sample_p5.pldat',
    urls: ['/data/sample_p5.pldat'],
  },
  {
    id: 'wmedf-xml',
    label: 'P2.8 – signal_055.wmedf + event_055.xml',
    urls: ['/data/signal_055.wmedf', '/data/event_055.xml'],
  },
  // Loose EDF+XML sets covering the supported custom-upload pairing cases (see
  // pair-signal-events). They load through the real pairing pipeline, so a
  // pairing regression shows up as a loaded day without events.
  {
    id: 'pairing-device-names',
    label: 'Pairing – device names (signal_033 + event_033)',
    urls: [
      '/data/custom-cases/device-names/signal_033.wmedf',
      '/data/custom-cases/device-names/event_033.xml',
    ],
  },
  {
    id: 'pairing-renamed-rank',
    label: 'Pairing – renamed files (recA/recB + x1/x2)',
    urls: [
      '/data/custom-cases/renamed-rank/recA.wmedf',
      '/data/custom-cases/renamed-rank/recB.wmedf',
      '/data/custom-cases/renamed-rank/x1.xml',
      '/data/custom-cases/renamed-rank/x2.xml',
    ],
  },
  {
    id: 'pairing-mixed-stems',
    label: 'Pairing – mixed stems (morning + therapy)',
    urls: [
      '/data/custom-cases/mixed-stems/morning.wmedf',
      '/data/custom-cases/mixed-stems/morning.xml',
      '/data/custom-cases/mixed-stems/therapy.wmedf',
      '/data/custom-cases/mixed-stems/event_therapy.xml',
    ],
  },
  // orphan.xml matches neither signal on purpose: selecting this entry should
  // show the unmatched-XML warning.
  {
    id: 'pairing-unmatched-warning',
    label: 'Pairing – unmatched XML warning (night1/night2 + orphan)',
    urls: [
      '/data/custom-cases/unmatched-warning/night1.wmedf',
      '/data/custom-cases/unmatched-warning/night2.wmedf',
      '/data/custom-cases/unmatched-warning/orphan.xml',
    ],
  },
];

// Mirrors how buildFileUploadData derives sourceFileName, so a demo entry can be
// matched back against the active file session.
const toSourceFileName = (entry: DemoFileEntry): string =>
  entry.urls
    .map((url) => url.split('/').pop() ?? url)
    .filter((name) => !/\.xml$/i.test(name))
    .join(', ');

export const findDemoFileIndex = (
  currentFileNames: string[] | undefined,
): number => {
  const sourceName = currentFileNames?.join(', ');
  if (!sourceName) return -1;

  return DEMO_FILES.findIndex(
    (entry) => toSourceFileName(entry) === sourceName,
  );
};

export const findSelectedDemoFileId = (
  currentFileNames: string[] | undefined,
): string | null => DEMO_FILES[findDemoFileIndex(currentFileNames)]?.id ?? null;

async function isHtmlFallback(resp: Response): Promise<boolean> {
  const contentType = resp.headers.get('content-type')?.toLowerCase() ?? '';
  if (contentType.includes('text/html')) return true;

  // We only read a tiny slice, not the whole file.
  const blob = await resp.clone().blob();
  const head = await blob.slice(0, 256).text();
  return /<!doctype html|<html[\s>]/i.test(head);
}

async function fetchDemoFile(url: string): Promise<File> {
  const fileName = url.split('/').pop() ?? url;
  const response = await fetch(url);

  // Dev/preview servers answer missing public assets with the SPA index page.
  if (!response.ok || (await isHtmlFallback(response))) {
    throw new Error(`Unable to load demo file "${fileName}".`);
  }

  const blob = await response.blob();
  return new File([blob], fileName, { type: 'application/octet-stream' });
}

export async function buildDemoUploadData(
  entry: DemoFileEntry,
): Promise<FileUploadData> {
  const files = await Promise.all(entry.urls.map(fetchDemoFile));
  return buildFileUploadData(files);
}
